// Completes a remote profile, like actions/thread_completion.js does for
// conversations: the own server only knows the posts of a remote account that
// reached it, so older posts are often missing.
//
// 1. The own server is asked to fetch the account again (search with resolve),
//    which refreshes name, bio, pictures and counts.
// 2. The origin server's public API gives the account's recent posts and pinned
//    posts (browser request without credentials; servers that require signed
//    requests or do not offer the Mastodon API answer with an error).
// 3. Each post the own server does not know yet is fetched by the own server
//    through a search with resolve; the server signs these requests itself.
// 4. The profile and its timelines are reloaded.

import { defineMessages } from "react-intl";

import api from "../api";

import { fetchAccount } from "./accounts";
import { showAlert } from "./alerts";
import { clearTimeline, expandAccountFeaturedTimeline, expandAccountTimeline } from "./timelines";

const messages = defineMessages({
  title: { id: "account.complete_profile", defaultMessage: "Complete profile" },
  done: { id: "account.complete_profile.done", defaultMessage: "Profile refreshed. {imported, plural, =0 {No new posts found.} one {# post added.} other {# posts added.}}" },
  originUnavailable: { id: "account.complete_profile.origin_unavailable", defaultMessage: "Profile refreshed. The original server did not share its posts, so no posts could be added." },
  failed: { id: "account.complete_profile.failed", defaultMessage: "The profile could not be completed." },
  started: { id: "account.complete_profile.started", defaultMessage: "Loading the profile from its server. This can take a moment." },
});

const PAGE_SIZE = 40;
const MAX_MISSING = 60;
const CONCURRENCY = 3;

const fetchJson = async (url) => {
  const response = await fetch(url, { credentials: "omit", headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw new Error(String(response.status));
  }
  return response.json();
};

// Recent and pinned public posts of the account on its origin server, or null.
const fetchOriginPosts = async (account) => {
  const address = account.get("url");
  const acct = account.get("acct");

  if (!address || !acct.includes("@")) {
    return null;
  }

  try {
    const origin = new URL(address).origin;
    const [username, domain] = acct.split("@");
    let remote;

    try {
      remote = await fetchJson(`${origin}/api/v1/accounts/lookup?acct=${encodeURIComponent(username)}`);
    } catch {
      // Split domain: the API may only know the full address.
      remote = await fetchJson(`${origin}/api/v1/accounts/lookup?acct=${encodeURIComponent(`${username}@${domain}`)}`);
    }

    if (!remote?.id) {
      return null;
    }

    const base = `${origin}/api/v1/accounts/${encodeURIComponent(remote.id)}/statuses`;
    const [recent, pinned] = await Promise.all([
      fetchJson(`${base}?limit=${PAGE_SIZE}&exclude_reblogs=true`),
      fetchJson(`${base}?pinned=true`).catch(() => []),
    ]);

    if (!Array.isArray(recent)) {
      return null;
    }

    return [...(Array.isArray(pinned) ? pinned : []), ...recent];
  } catch {
    return null;
  }
};

const resolveOnOwnServer = async (getState, q, type) => {
  const response = await api(getState).get("/api/v2/search", { params: { q, resolve: true, limit: 1, type } });
  return (response.data[type] || []).length > 0;
};

export const completeAccountProfile = (accountId) => async (dispatch, getState) => {
  const account = getState().getIn(["accounts", accountId]);

  if (!account) {
    return;
  }

  let imported = 0;
  let posts = null;

  dispatch(showAlert(messages.title, messages.started));

  try {
    try {
      await resolveOnOwnServer(getState, account.get("url") || `@${account.get("acct")}`, "accounts");
    } catch {
      // the posts below can still be completed
    }

    posts = await fetchOriginPosts(account);

    if (posts) {
      const known = new Set();
      getState().get("statuses").forEach(item => {
        known.add(item.get("uri"));
        known.add(item.get("url"));
      });

      const missing = [];
      posts.forEach(item => {
        const uri = item.uri || item.url;
        if (uri && !known.has(item.uri) && !known.has(item.url) && !missing.includes(uri)) {
          missing.push(uri);
        }
      });

      const queue = missing.slice(0, MAX_MISSING);
      const worker = async () => {
        while (queue.length > 0) {
          const uri = queue.shift();
          try {
            if (await resolveOnOwnServer(getState, uri, "statuses")) {
              imported += 1;
            }
          } catch {
            // a single post that cannot be fetched must not stop the others
          }
        }
      };

      await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    }
  } catch {
    dispatch(showAlert(messages.title, messages.failed));
    return;
  }

  dispatch(fetchAccount(accountId));

  // Reload the loaded timelines from scratch: added posts are older than the
  // newest one, so a normal refresh would not show them.
  const reloads = [
    [`account:${accountId}`, () => expandAccountTimeline(accountId)],
    [`account:${accountId}:with_replies`, () => expandAccountTimeline(accountId, { withReplies: true })],
    [`account:${accountId}:pinned`, () => expandAccountFeaturedTimeline(accountId)],
  ];
  reloads.forEach(([timeline, reload]) => {
    if (getState().hasIn(["timelines", timeline])) {
      dispatch(clearTimeline(timeline));
      dispatch(reload());
    }
  });

  dispatch(showAlert(messages.title, posts ? messages.done : messages.originUnavailable, { imported }));
};
